/**
 * Authentication Helper Module
 * Manages JWT token-based authentication with localStorage
 */

const AUTH_CONFIG = {
  TOKEN_KEY: 'auth_token',
  USER_KEY: 'user_info',
  LOGIN_URL: '/api/v1/auth/login',
  LOGIN_PAGE: '/login.html',
  HOME_PAGE: '/index.html',
  // Otomatik logout ayarları (dakika cinsinden)
  SESSION_TIMEOUT_MIN: 30,    // 30 dakika hareketsizlik sonrası logout
  WARNING_BEFORE_MIN: 2,      // Logout'tan 2 dakika önce uyarı göster
  CHECK_INTERVAL_SEC: 30      // Her 30 saniyede bir kontrol et
};

// Popup monitor modunda mı? (?popup=1 parametresi)
const _isPopupMode = (typeof URLSearchParams !== 'undefined') &&
  new URLSearchParams(window.location.search).get('popup') === '1';

/**
 * Decode JWT token without verification (client-side)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (e) {
    console.error('JWT decode error:', e);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
function isTokenExpired(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get stored authentication token
 * @returns {string|null} Token or null if not found/expired
 */
function getToken() {
  const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    clearCredentials(); // Clear expired token without redirect
    return null;
  }
  
  return token;
}

/**
 * Get stored user information
 * @returns {object|null} User info or null
 */
function getUserInfo() {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch (e) {
    console.error('Failed to parse user info:', e);
    return null;
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if valid token exists
 */
function isAuthenticated() {
  return getToken() !== null;
}

/**
 * Login with username and password
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise<object>} Login response with token and user info
 */
async function login(username, password) {
  try {
    const response = await fetch(AUTH_CONFIG.LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    
    // Check if login was successful
    // Support APIs that return {success: true, token, user} or just {token, user}
    if (data.success === false) {
      throw new Error(data.message || 'Login failed');
    }
    
    // Expected response: { success: true, token: "jwt...", user: {...} }
    if (!data.token) {
      throw new Error('No token received from server');
    }

    // Store token and user info
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, data.token);
    if (data.user) {
      const u = data.user;
      // Support both PascalCase (NexBus API) and snake_case field names
      const userInfo = {
        UserId: u.UserId || u.user_id,
        UserName: u.UserName || u.username,
        FirstName: u.FirstName || u.first_name,
        LastName: u.LastName || u.last_name,
        Email: u.Email || u.email,
        GroupPermission: u.GroupPermission || u.group_permission,
        DefaultPageParent: u.DefaultPageParent || u.default_page_parent,
        Role: u.Role || u.role || 'user'
      };
      localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(userInfo));
      
      // Store default page for redirect after login
      const defPage = userInfo.DefaultPageParent;
      if (defPage) {
        sessionStorage.setItem('default_page_parent', defPage);
      }
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Clear stored credentials without redirecting (internal use)
 */
function clearCredentials() {
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  sessionStorage.removeItem('default_page_parent');
}

/**
 * Logout - clear stored credentials and redirect to login page
 */
function logout() {
  clearCredentials();
  window.location.href = AUTH_CONFIG.LOGIN_PAGE;
}

/**
 * Redirect to login page if not authenticated
 * Call this on protected pages
 */
function requireAuth() {
  if (!isAuthenticated()) {
    // Store current page to redirect back after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirect_after_login', currentPath);
    
    window.location.href = AUTH_CONFIG.LOGIN_PAGE;
    return false;
  }
  return true;
}

/**
 * Redirect to home page if already authenticated
 * Call this on login page
 */
function redirectIfAuthenticated() {
  if (isAuthenticated()) {
    const redirectTo = sessionStorage.getItem('redirect_after_login') || AUTH_CONFIG.HOME_PAGE;
    sessionStorage.removeItem('redirect_after_login');
    window.location.href = redirectTo;
    return true;
  }
  return false;
}

/**
 * Create fetch wrapper with automatic token injection
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
async function authenticatedFetch(url, options = {}) {
  const token = getToken();
  
  if (!token) {
    throw new Error('No authentication token available');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };

  const response = await fetch(url, {
    ...options,
    headers
  });

  // Auto logout on 401 Unauthorized
  if (response.status === 401) {
    // Popup modda logout yapma — WebSocket verisi akmaya devam eder
    if (_isPopupMode) {
      console.warn('[Auth] Popup modda 401 alındı, oturum süresi dolmuş olabilir. Logout yapılmıyor.');
      throw new Error('Session expired (popup mode - no redirect)');
    }
    logout(); // clears credentials and redirects to login
    throw new Error('Session expired');
  }

  return response;
}

/**
 * PLC yazma yetkisini kontrol eder.
 * `user` rolü yazamaz; `operator`, `admin` ve `super_admin` yazabilir.
 * Eski sistemlerde rol bilgisi olmayan kullanıcılar için GroupPermission=1 (Admin) da kabul edilir.
 */
function canWritePlc() {
  try {
    const info = getUserInfo();
    if (!info) return false;
    const role = String(info.Role || info.role || '').toLowerCase();
    if (role === 'operator' || role === 'admin' || role === 'super_admin' || role === 'superadmin' || role === 'root') {
      return true;
    }
    // Geriye donuk: rol atanmamis kullanicilar icin GroupPermission=1 (Admin) veya
    // ozel username (datarapor/superadmin/admin) admin kabul edilir.
    const uname = String(info.UserName || info.username || '').toLowerCase();
    if (uname === 'datarapor' || uname === 'superadmin' || uname === 'admin') return true;
    const gp = info.GroupPermission || info.group_permission;
    if (gp === 1 || gp === '1') return true;
    return false;
  } catch (e) {
    return false;
  }
}

// Export functions for global use
window.Auth = {
  login,
  logout,
  isAuthenticated,
  getToken,
  getUserInfo,
  requireAuth,
  redirectIfAuthenticated,
  authenticatedFetch,
  startSessionTimer,
  stopSessionTimer,
  canWritePlc
};

// ===========================
// Otomatik Logout (Inaktivite Tabanlı)
// ===========================

const SessionTimer = (function() {
  let _lastActivity = Date.now();
  let _checkInterval = null;
  let _warningShown = false;
  let _warningModal = null;
  let _countdownInterval = null;

  let TIMEOUT_MS = AUTH_CONFIG.SESSION_TIMEOUT_MIN * 60 * 1000;
  let WARNING_MS = AUTH_CONFIG.WARNING_BEFORE_MIN * 60 * 1000;
  const CHECK_MS = AUTH_CONFIG.CHECK_INTERVAL_SEC * 1000;

  function _resetActivity() {
    _lastActivity = Date.now();
    // Uyarı gösteriliyorsa ve kullanıcı aktifse kapat
    if (_warningShown) {
      _hideWarning();
    }
  }

  const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

  function _throttle(fn, ms) {
    let last = 0;
    return function() {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn();
      }
    };
  }

  const _throttledReset = _throttle(_resetActivity, 5000); // 5 saniyede bir güncelle

  function _attachListeners() {
    ACTIVITY_EVENTS.forEach(evt => {
      document.addEventListener(evt, _throttledReset, { passive: true });
    });
  }

  function _detachListeners() {
    ACTIVITY_EVENTS.forEach(evt => {
      document.removeEventListener(evt, _throttledReset);
    });
  }

  function _createWarningModal() {
    if (document.getElementById('sessionTimeoutModal')) return;

    const modalHTML = `
      <div id="sessionTimeoutModal" class="modal fade" tabindex="-1" data-coreui-backdrop="static" data-coreui-keyboard="false">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-warning text-dark">
              <h5 class="modal-title">
                <svg class="icon me-2" style="width:20px;height:20px"><use xlink:href="vendors/@coreui/icons/svg/free.svg#cil-warning"></use></svg>
                Oturum Zaman Aşımı
              </h5>
            </div>
            <div class="modal-body text-center">
              <p class="mb-2">Uzun süredir işlem yapmadınız.</p>
              <p class="mb-3">Oturumunuz <strong id="sessionCountdown">--</strong> saniye sonra sonlandırılacak.</p>
            </div>
            <div class="modal-footer justify-content-center">
              <button type="button" class="btn btn-primary px-4" id="sessionKeepAliveBtn">Oturumu Devam Ettir</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    document.getElementById('sessionKeepAliveBtn').addEventListener('click', function() {
      _resetActivity();
    });
  }

  function _showWarning() {
    if (_warningShown) return;
    _warningShown = true;
    _createWarningModal();

    const modalEl = document.getElementById('sessionTimeoutModal');
    if (!modalEl) return;

    // CoreUI/Bootstrap Modal
    if (window.coreui && window.coreui.Modal) {
      _warningModal = new coreui.Modal(modalEl);
    } else if (window.bootstrap && window.bootstrap.Modal) {
      _warningModal = new bootstrap.Modal(modalEl);
    } else {
      // Fallback: doğrudan göster
      modalEl.classList.add('show');
      modalEl.style.display = 'block';
      modalEl.removeAttribute('aria-hidden');
    }

    if (_warningModal) _warningModal.show();

    // Geri sayım başlat
    const countdownEl = document.getElementById('sessionCountdown');
    let remaining = Math.ceil(WARNING_MS / 1000);

    if (countdownEl) countdownEl.textContent = remaining;

    _countdownInterval = setInterval(function() {
      remaining--;
      if (countdownEl) countdownEl.textContent = Math.max(remaining, 0);
      if (remaining <= 0) {
        clearInterval(_countdownInterval);
      }
    }, 1000);
  }

  function _hideWarning() {
    _warningShown = false;
    if (_countdownInterval) {
      clearInterval(_countdownInterval);
      _countdownInterval = null;
    }
    if (_warningModal) {
      _warningModal.hide();
      _warningModal = null;
    } else {
      const modalEl = document.getElementById('sessionTimeoutModal');
      if (modalEl) {
        modalEl.classList.remove('show');
        modalEl.style.display = 'none';
      }
    }
  }

  function _check() {
    // Login sayfasındaysa kontrol etme
    if (window.location.pathname.endsWith('login.html')) return;

    // Token yoksa zaten çıkmış
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (!token) {
      stop();
      return;
    }

    // Token süresi dolmuşsa direkt çıkış
    if (isTokenExpired(token)) {
      stop();
      logout();
      return;
    }

    const idle = Date.now() - _lastActivity;

    // Uyarı zamanı geldiyse göster
    if (idle >= TIMEOUT_MS - WARNING_MS && !_warningShown) {
      _showWarning();
    }

    // Timeout süresi dolduysa logout
    if (idle >= TIMEOUT_MS) {
      stop();
      _hideWarning();
      clearCredentials();
      // Login sayfasına yönlendir, timeout sebebini belirt
      sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
      sessionStorage.setItem('session_timeout', '1');
      window.location.href = AUTH_CONFIG.LOGIN_PAGE;
    }
  }

  function setTimeout(minutes) {
    if (minutes && minutes >= 5 && minutes <= 480) {
      TIMEOUT_MS = minutes * 60 * 1000;
      WARNING_MS = Math.min(AUTH_CONFIG.WARNING_BEFORE_MIN * 60 * 1000, TIMEOUT_MS / 2);
      console.log(`[Auth] Oturum timeout güncellendi: ${minutes} dk`);
    }
  }

  function start() {
    if (_checkInterval) return; // Zaten çalışıyor
    _lastActivity = Date.now();
    _attachListeners();
    _checkInterval = setInterval(_check, CHECK_MS);
    console.log(`[Auth] Oturum zamanlayıcısı başlatıldı (${Math.round(TIMEOUT_MS / 60000)} dk timeout)`);
  }

  function stop() {
    _detachListeners();
    if (_checkInterval) {
      clearInterval(_checkInterval);
      _checkInterval = null;
    }
    _hideWarning();
  }

  return { start, stop, setTimeout };
})();

function startSessionTimer() {
  SessionTimer.start();
}

function stopSessionTimer() {
  SessionTimer.stop();
}

// Sayfa yüklendiğinde, giriş yapmışsa zamanlayıcıyı otomatik başlat
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function() {
    // Login sayfasında başlatma
    if (window.location.pathname.endsWith('login.html')) {
      // Timeout sonrası login sayfasına düştüyse mesaj göster
      if (sessionStorage.getItem('session_timeout') === '1') {
        sessionStorage.removeItem('session_timeout');
        setTimeout(function() {
          const errorDiv = document.getElementById('loginError');
          if (errorDiv) {
            errorDiv.textContent = 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
            errorDiv.classList.remove('d-none');
          }
        }, 100);
      }
      return;
    }

    // Popup monitor modunda oturum zamanlayıcısını başlatma
    // Popup sürekli açık kalacak izleme penceresi, etkileşim olmaz
    if (_isPopupMode) {
      console.log('[Auth] Popup modu — oturum zamanlayıcısı devre dışı');
      return;
    }

    // Token varsa oturum zamanlayıcısını başlat
    if (localStorage.getItem(AUTH_CONFIG.TOKEN_KEY)) {
      // Config'den timeout süresini oku, sonra zamanlayıcıyı başlat
      fetch('/api/ui-settings/menu').then(r => r.ok ? r.json() : null).then(cfg => {
        if (cfg && cfg.sessionTimeoutMin) {
          SessionTimer.setTimeout(cfg.sessionTimeoutMin);
        }
        startSessionTimer();
      }).catch(() => {
        startSessionTimer(); // Config okunamazsa default ile başlat
      });
    }
  });
}
